import { ApiError, ProblemType } from '@/domains/api';
import type { IRankingList } from './api/ranking-contracts';
import type { IRankingEdition } from './api/ranking-process-contracts';

/**
 * Rankings on this side (SR-222 block F6): where each list goes on the page — one mirror, of the
 * Lightning page — and the words a placement's history draws survey-api's vocabularies in. Which
 * survey a part's placements are kept under is the catalogue's `keptUnder` (served by
 * survey-api `30d88f37`, adopted by block F12), named by `figures.ts`' `KEPT_UNDER_WORDS`;
 * the mirror that stood here, `RANKING_SURVEY_OF`, is gone with the gap.
 *
 * **`RANKING_LAYOUT` mirrors the Lightning page, and is the owner's to change** (the roadmap's
 * U9): FA's lists in two columns, every other survey's in one. **The catalogue decides what
 * exists, the layout only where it goes** (`columnsOf`): a list the catalogue sends and the layout
 * does not name is appended to the left column in the catalogue's order — never dropped — and a
 * list the layout names and the catalogue no longer sends is not drawn. Rejected: a column per
 * list in the catalogue's order — FA's eight lists side by side are wider than the page, and the
 * research team reads them as Salesforce lays them out.
 *
 * **The rest are words, not mirrors.** A value they lack is drawn as spelled (`wordFor`), never
 * dropped — survey-api's origins and event kinds are open sets on this side.
 */

/** One survey's lists by column, each in the page's order. A survey the table does not name
 *  draws its lists in one column, the catalogue's order. */
export interface IRankingLayout {
  left: readonly string[];
  right: readonly string[];
}

/** The Lightning page's columns (U9). See the module's comment. */
export const RANKING_LAYOUT: Readonly<Record<string, IRankingLayout>> = {
  FA: {
    left: ['top', 'bis', 'mothers', 'va_biz'],
    right: ['next_gen', 'next_gen_bis', 'women', 'women_bis']
  }
};

/** A survey's lists split into the page's two columns — `right` empty for a one-column survey. */
export interface IRankingColumns {
  left: IRankingList[];
  right: IRankingList[];
}

/** `lists` — the catalogue's, as sent — placed by `RANKING_LAYOUT`: the layout's order inside a
 *  column, a list the layout does not name appended to the left in the catalogue's order, a list
 *  the catalogue does not send left out. */
export function columnsOf(surveyCode: string, lists: readonly IRankingList[]): IRankingColumns {
  const layout = RANKING_LAYOUT[surveyCode];
  if (layout === undefined) return { left: [...lists], right: [] };
  const sent = new Map(lists.map((list) => [list.list, list]));
  const placed = (codes: readonly string[]) => codes.flatMap((code) => sent.get(code) ?? []);
  const named = new Set([...layout.left, ...layout.right]);
  return {
    left: [...placed(layout.left), ...lists.filter((list) => !named.has(list.list))],
    right: placed(layout.right)
  };
}

/** Where a placement came from, as its history says above the rows. */
export const ORIGIN_WORDS: ReadonlyMap<string, string> = new Map([
  ['migration', 'Imported from Salesforce'],
  ['staff', 'Entered by staff']
]);

/** A draft's row origin, in a column: the scored result's import or a staff edit (SR-231). */
export const ROW_ORIGIN_WORDS: ReadonlyMap<string, string> = new Map([
  ['import', 'Import'],
  ['staff', 'Staff edit'],
  ['migration', 'Salesforce']
]);

/** survey-api's `RankingFlags` codes (SR-506) in a word, in the order a summary counts them. */
export const RANKING_FLAG_WORDS: ReadonlyMap<string, string> = new Map([
  ['gender-mismatch', 'gender'],
  ['over-age', 'age'],
  ['territory-mismatch', 'territory']
]);

/** What one event of a placement's history did. */
export const RANKING_EVENT_WORDS: ReadonlyMap<string, string> = new Map([
  ['placement_set', 'Set'],
  ['placement_removed', 'Removed']
]);

// The ranking process (SR-231) — the editions, a draft's renumbering and the import. Words, not
// mirrors: a status, a refusal's reason or a journal kind this table lacks is drawn as spelled.

/** A ranking's `status` as its chip: a draft is under way, a published ranking settled. */
export function rankingStatusChip(status: string): { label: string; tone: 'info' | 'success' | 'neutral' } {
  if (status === 'draft') return { label: 'Draft', tone: 'info' };
  if (status === 'published') return { label: 'Published', tone: 'success' };
  return { label: status, tone: 'neutral' };
}

/** What an `invalid-transition` refusal's `reason` says (design §8). */
export const RANKING_REFUSAL_WORDS: ReadonlyMap<string, string> = new Map([
  ['already-placed', 'Already in this ranking — move them instead.'],
  [
    'ranking-published',
    'This ranking has been published, so it can no longer be changed. It has been read again.'
  ],
  [
    'ranking-changed',
    'The draft changed while the file was being checked — check the file again before applying it.'
  ],
  ['ranking-draft', 'A draft is changed only from its ranking’s page.'],
  ['ranking-not-contiguous', 'Not every territory is numbered 1 to N.'],
  [
    'cycle-from-import',
    'This ranking’s cycle is the one its scored result was exported from. To change it, export the scoring input from another cycle and import its scored result.'
  ],
  [
    'cycle-fixed',
    'This edition’s cycle is fixed: its snapshot and its scores are that cycle’s. A ranking on another cycle is another edition — start one.'
  ],
  [
    'freeze-backwards',
    'The snapshot was not updated: the moment chosen is not after the snapshot this ranking holds, and a snapshot only moves forward. Nothing was changed.'
  ]
]);

/** The `reason` of survey-api's `invalid-transition` refusal of a ranking write, or `null` for any
 *  other answer. */
export function rankingRefusalReason(error: unknown): Nullable<string> {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.invalidTransition)) return null;
  return String(error.problem?.reason ?? '');
}

/** A count as the screens say it — "2,851". */
export function countText(n: number): string {
  return n.toLocaleString('en-US');
}

/** A journal event's kind, as the History tab says it. */
export const RANKING_JOURNAL_WORDS: ReadonlyMap<string, string> = new Map([
  ['imported', 'Imported'],
  ['placement_inserted', 'Inserted'],
  ['placement_moved', 'Moved'],
  ['placement_removed', 'Removed'],
  ['published', 'Published'],
  ['cycle_linked', 'Cycle linked'],
  ['finalists_imported', 'Finalists returned'],
  ['returned_to_draft', 'Back to draft'],
  ['frozen', 'Snapshot taken'],
  ['threshold_set', 'Threshold set']
]);

/** An edition the export of record made and no scored result has filled yet (SR-451): a draft,
 *  frozen, nothing placed, nothing imported. An answer from before SR-415 or SR-451, which says
 *  nothing of `imported` or `frozenAt`, is never read as one — nor a draft a staff placement made
 *  and Back to draft emptied, which no export froze. */
export function exportedNotImported(edition: IRankingEdition): boolean {
  return (
    edition.status === 'draft' &&
    edition.placements === 0 &&
    edition.imported === false &&
    edition.frozenAt != null
  );
}

/** An edition made on its cycle and not exported yet (SR-451): a draft, empty, no snapshot, no
 *  scored result. An answer from before SR-451, which says nothing of `imported`, is never read
 *  as one. */
export function madeNotExported(edition: IRankingEdition): boolean {
  return (
    edition.status === 'draft' &&
    edition.placements === 0 &&
    edition.imported === false &&
    !edition.frozenAt
  );
}

/** What the editions say of such an edition in place of its count. */
export const EXPORTED_NOT_IMPORTED = 'Exported, not imported yet';

/** What a ranking's snapshot of the metrics archive takes in (SR-451; RNK-09's "fixed, dated
 *  snapshot", the word the requirements use — `frozen_at` on the wire): one sentence for the
 *  export's note and the snapshot dialog, so the two never drift. */
export const SNAPSHOT_READ =
  'nothing recorded after it is read — an advisor’s answer, a staff edit or a firm’s file alike';

/** What the snapshot leaves as it is: the answer columns read the responses as they are now. */
export const SNAPSHOT_ANSWERS =
  'Answer columns are not in the snapshot: they read the cycle’s responses as they are now.';
